import React, { useState, useEffect } from 'react';
import { fetchGalleryBySlug } from '../lib/cms';

export default function Gallery({ slug }) {
  const [gallery, setGallery] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      const data = await fetchGalleryBySlug(slug);
      if (data) {
        setGallery(data);
      }
    }
    loadGallery();
  }, [slug]);

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  // Filter out the specific overview plan image from the gallery
  const images = gallery.images.filter(imgUrl => 
    !imgUrl.includes('plads-oversigt-22-05-2026-sheet-a103-oversigtplan-hytte-conv-1-scaled.png')
  );

  const openLightbox = (index) => {
    setActiveImageIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setActiveImageIndex(null);
    document.body.style.overflow = '';
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="my-10">
      <h3 className="text-2xl font-serif font-semibold text-[#0f0d0e] mb-6 text-center">
        {gallery.title?.da || gallery.title}
      </h3>
      
      {/* Grid Layout */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((imgUrl, idx) => {
          // Optimize image sizes using Cloudflare resizing
          const optimizedUrl = imgUrl.startsWith('https://assets.auxodev.dk')
            ? imgUrl.replace('https://assets.auxodev.dk/assets', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets')
            : imgUrl;

          return (
            <div 
              key={idx} 
              className="group relative aspect-4/3 overflow-hidden rounded-lg bg-stone-100 shadow-sm cursor-pointer hover:shadow-md transition-all duration-300"
              onClick={() => openLightbox(idx)}
            >
              <img
                src={optimizedUrl}
                alt={`${gallery.title?.da || 'Galleri'} - Billede ${idx + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
          );
        })}
      </div>

      {/* Lightbox Overlay */}
      {activeImageIndex !== null && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 md:p-10 select-none animate-fade-in"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            className="absolute top-6 right-6 text-white/80 hover:text-white text-3xl focus:outline-none transition-colors p-2 z-50"
            onClick={closeLightbox}
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Left Arrow */}
          <button 
            className="absolute left-4 md:left-8 text-white/80 hover:text-white text-4xl focus:outline-none transition-colors p-3 bg-black/30 hover:bg-black/50 rounded-full z-40"
            onClick={prevImage}
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>

          {/* Image Container */}
          <div className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center">
            <img
              src={images[activeImageIndex]}
              alt={`Billede ${activeImageIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded shadow-2xl animate-scale-up"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-[-40px] text-white/80 text-sm font-medium">
              Billede {activeImageIndex + 1} af {images.length}
            </div>
          </div>

          {/* Right Arrow */}
          <button 
            className="absolute right-4 md:right-8 text-white/80 hover:text-white text-4xl focus:outline-none transition-colors p-3 bg-black/30 hover:bg-black/50 rounded-full z-40"
            onClick={nextImage}
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}
